import {
  BUYBACK_MAX_NEEDS_FIX_COUNT,
  buybackIdentificationDir,
  CANCELABLE_DEVICE_BUYBACK_STATUSES,
  type DeviceBuyback,
  DeviceBuybackStatus,
  deviceBuybackPath,
  type IdentificationKind,
  type IdentificationSide,
  requiredIdentificationSides,
} from '@maru44/huntre-utils/src/models/deviceBuyback'
import { getData } from '@maru44/huntre-utils/src/utils/data'
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { useCallback } from 'react'
import { useStorage } from 'src/hooks/storage/useStorage'
import { ValidationError } from 'src/utils/error'
import { firestore } from 'src/utils/firebase'
import useSWR from '../useSWR'

/** 買取申請の詳細と、申請者ができる操作 */
export const useDeviceBuyback = (clientId: string | undefined, buybackId: string | undefined) => {
  const { uploadFileToPath } = useStorage()
  const docPath = deviceBuybackPath(clientId ?? '-', buybackId ?? '-')

  const getDeviceBuyback = useSWR(
    [docPath, clientId, buybackId],
    async () => {
      const ref = doc(firestore, docPath)
      const snap = await getDoc(ref)
      if (!snap.exists()) {
        throw new Error(`deviceBuyback not found: ${buybackId}`)
      }
      return getData<DeviceBuyback>(snap)
    },
    { disabled: !clientId || !buybackId },
  )

  /**
   * 本人確認書類をアップロードする。
   * 申請を作ってからでないと Storage のルールが通らない
   * (clients/{clientId}/deviceBuybacks/{id}.applicantId を見るため)。
   */
  const uploadIdentification = useCallback(
    async (side: IdentificationSide, file: File) => {
      if (!clientId || !buybackId) return
      try {
        const storagePath = `${buybackIdentificationDir(clientId, buybackId)}/${side}_${Date.now()}_${file.name}`
        await uploadFileToPath(storagePath, file)
        await updateDoc(doc(firestore, docPath), {
          [`identificationImagePaths.${side}`]: storagePath,
          updatedAt: serverTimestamp(),
        })
        return storagePath
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    [clientId, buybackId, docPath, uploadFileToPath],
  )

  /** 発送前のキャンセル */
  const cancelDeviceBuyback = useCallback(async () => {
    if (!clientId || !buybackId) return
    try {
      const ref = doc(firestore, docPath)
      const snap = await getDoc(ref)
      if (!snap.exists()) {
        throw new Error(`deviceBuyback not found: ${buybackId}`)
      }
      const current = getData<DeviceBuyback>(snap)
      if (!CANCELABLE_DEVICE_BUYBACK_STATUSES.includes(current.status)) {
        throw new ValidationError('発送後はキャンセルできません')
      }
      await updateDoc(ref, { status: DeviceBuybackStatus.Canceled, updatedAt: serverTimestamp() })
    } catch (e) {
      console.error(e)
      throw e
    }
  }, [clientId, buybackId, docPath])

  /**
   * 差し戻しへの再提出。書類を差し替えて applied に戻す。
   * ルール側でも needsFixCount <= 3 を課している。
   */
  const resubmitIdentification = useCallback(
    async (kind: IdentificationKind, files: Partial<Record<IdentificationSide, File>>) => {
      if (!clientId || !buybackId) return
      try {
        const ref = doc(firestore, docPath)
        const snap = await getDoc(ref)
        if (!snap.exists()) {
          throw new Error(`deviceBuyback not found: ${buybackId}`)
        }
        const current = getData<DeviceBuyback>(snap)
        if (current.status !== DeviceBuybackStatus.NeedsFix) {
          throw new ValidationError('再提出できる状態ではありません')
        }
        if (current.needsFixCount + 1 > BUYBACK_MAX_NEEDS_FIX_COUNT) {
          throw new ValidationError('再提出の上限に達しました。お問合せください')
        }

        // 要求される面は書類で変わる (マイナンバーカードは表のみ)
        const paths: Record<string, string> = {}
        for (const side of requiredIdentificationSides(kind)) {
          const f = files[side]
          if (!f) throw new ValidationError('必要な面の画像が揃っていません')
          const storagePath = `${buybackIdentificationDir(clientId, buybackId)}/${side}_${Date.now()}_${f.name}`
          await uploadFileToPath(storagePath, f)
          paths[side] = storagePath
        }
        await updateDoc(ref, {
          status: DeviceBuybackStatus.Applied,
          identificationKind: kind,
          identificationImagePaths: paths,
          needsFixCount: current.needsFixCount + 1,
          updatedAt: serverTimestamp(),
        })
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    [clientId, buybackId, docPath, uploadFileToPath],
  )

  return { getDeviceBuyback, uploadIdentification, cancelDeviceBuyback, resubmitIdentification }
}
